/**
 * 轻提示（Toast）
 * ---------------------------------------------------------------------------
 * 对应设计稿 .toast：底部居中的深色胶囊。
 * 只用于"做完了"这类不需要用户确认的反馈；需要用户决策的一律用弹窗。
 *
 * 用法：
 *   const toast = useToast();
 *   toast.success('订单已完成');
 *   toast.error(error.message);
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons';
import { ToastContext, type ToastApi } from '@/components/ui/toast-store';

type ToastTone = 'success' | 'error' | 'info';

type ToastItem = {
  id: number;
  tone: ToastTone;
  message: string;
};

/** 提示停留时长。收银场景下别太短，前台可能正低头收钱 */
const DURATION_MS = 2800;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const push = useCallback((tone: ToastTone, message: string) => {
    const id = nextId.current;
    nextId.current += 1;
    setItems((current) => [...current, { id, tone, message }]);
    window.setTimeout(() => {
      setItems((current) => current.filter((item) => item.id !== id));
    }, DURATION_MS);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* aria-live 让读屏用户也能听到反馈，不然这类提示对他们等于不存在 */}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-7 left-1/2 z-[200] flex -translate-x-1/2 flex-col items-center gap-2"
      >
        {items.map((item) => (
          <div
            key={item.id}
            className="flex animate-[toast-in_.24s_ease-out] items-center gap-2.5 rounded-full bg-[#24261F] px-[18px] py-[11px] text-table text-white shadow-e3"
          >
            <span
              className={cn(
                'grid size-[18px] shrink-0 place-items-center rounded-full',
                item.tone === 'success' && 'bg-green-fill',
                item.tone === 'error' && 'bg-red-fill',
                item.tone === 'info' && 'bg-blue-fill',
              )}
            >
              <Icon
                name={item.tone === 'success' ? 'check' : item.tone === 'error' ? 'close' : 'bulb'}
                size={11}
                strokeWidth={3}
              />
            </span>
            {item.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
